import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Popover,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import moment from "moment";
import { useDispatch } from "react-redux";
import { NotificationAlert } from "../../store/crm/call-log";

const PRIMARY = "#aa2167";

// Bell + dropdown. The notification data is fetched from the NotificationAlert
// API (call-log) when the bell is clicked, and each alert/reminder is shown as
// a notification row.
const NotificationsBell = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const open = Boolean(anchorEl);
  const count = list.length;

  const fetchNotifications = async () => {
    setLoading(true);
    const response = await dispatch(NotificationAlert());
    setList(Array.isArray(response?.payload) ? response.payload : []);
    setLoading(false);
  };

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchNotifications();
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          onClick={handleOpen}
          sx={{
            background: open ? "#FAEFF5" : "#fff",
            border: "1px solid #F0CDDB",
            borderRadius: "12px",
            width: 44,
            height: 44,
            color: PRIMARY,
            "&:hover": { background: "#FAEFF5" },
          }}
        >
          <Badge
            badgeContent={count}
            color="error"
            overlap="circular"
            sx={{ "& .MuiBadge-badge": { fontSize: "10px", fontWeight: 700, minWidth: 16, height: 16 } }}
          >
            <NotificationsNoneRoundedIcon sx={{ fontSize: 24 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 360,
            maxWidth: "92vw",
            borderRadius: "14px",
            boxShadow: "0 12px 40px rgba(17,24,39,0.16)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#111827" }}>
            Notifications
          </Typography>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#9CA3AF" }}>
            ({count})
          </Typography>
        </Box>
        <Divider />

        {/* List */}
        {loading ? (
          <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={26} sx={{ color: PRIMARY }} />
          </Box>
        ) : count === 0 ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography sx={{ color: "#9CA3AF", fontSize: "0.9rem" }}>No notifications</Typography>
          </Box>
        ) : (
          <Box sx={{
            maxHeight: 420,
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: `${PRIMARY} #FAEFF5`,
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "#FAEFF5", borderRadius: "8px" },
            "&::-webkit-scrollbar-thumb": { background: PRIMARY, borderRadius: "8px" },
            "&::-webkit-scrollbar-thumb:hover": { background: "#8e1a55" },
          }}>
            {list.map((row, idx) => (
              <Box key={row?.id ?? idx}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: 2.5, py: 1.75 }}>
                  {/* Avatar */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      background: "#fde7ef",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <NotificationsActiveOutlinedIcon sx={{ color: PRIMARY, fontSize: 22 }} />
                  </Box>

                  {/* Message + meta */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        color: "#111827",
                        lineHeight: 1.35,
                      }}
                    >
                      Reminder on {row?.date ? moment(row.date).format("MMM D, YYYY") : "—"}
                    </Typography>

                    {row?.note && (
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: "#111827",
                          mt: 0.25,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {row.note}
                      </Typography>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5, gap: 1 }}>
                      {row?.staff?.name && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                          <PersonOutlineRoundedIcon sx={{ color: PRIMARY, fontSize: 14 }} />
                          <Typography sx={{ fontSize: "0.72rem", color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {row.staff.name}
                          </Typography>
                        </Box>
                      )}
                      <Typography sx={{ fontSize: "0.72rem", color: "#111827", flexShrink: 0 }}>
                        {row?.created_at ? moment(row.created_at).fromNow() : ""}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {idx < count - 1 && <Divider sx={{ mx: 2.5 }} />}
              </Box>
            ))}
          </Box>
        )}
      </Popover>
    </>
  );
};

export default NotificationsBell;
